import { useEffect, useMemo, useState } from 'react';
import { apiGet } from '@/lib/api';
import { Card, ErrorNote, Skeleton } from '@/components/ui';
import {
  countBlocking,
  DEFAULT_OPTIONS,
  exportQuery,
  exportSummary,
  optionsFromPolicy,
  orderIssues,
  QUESTIONS,
  type CheckResponse,
  type Options,
} from '@/lib/baLawExport';

/**
 * The BA Law export, with the questions that are still open with them on the face
 * of it.
 *
 * The answers are not buried in the writer: they are query parameters the API
 * resolves through one policy object (`services/ba-law/policy.ts`), so a second
 * version of the sheet — the whole aircraft rather than only his flying, say — is
 * a different download rather than a deploy. Changing one re-runs the check, so
 * the consequences show before anything is sent to a lawyer.
 */
export default function BaLawExport() {
  const [options, setOptions] = useState<Options>(DEFAULT_OPTIONS);
  const [check, setCheck] = useState<CheckResponse | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const qs = useMemo(() => exportQuery(options), [options]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    apiGet<CheckResponse>(`/api/export/ba-law/check${qs}`)
      .then((d) => {
        if (!live) return;
        setCheck(d);
        setError('');
      })
      .catch((e) => {
        if (live) setError(e instanceof Error ? e.message : 'Could not read the export');
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [qs]);

  // The server is the authority on the defaults; adopt them once, on first load.
  const [adopted, setAdopted] = useState(false);
  useEffect(() => {
    if (check && !adopted) {
      setOptions(optionsFromPolicy(check.policy));
      setAdopted(true);
    }
  }, [check, adopted]);

  const issues = check ? orderIssues(check.issues) : [];
  const blocking = check ? countBlocking(check.issues) : 0;

  return (
    <Card className="mb-6 p-5 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-xl">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-300">BA Law flight log</h2>
          <p className="mt-1 text-xs text-slate-500">
            Their own {check?.policy.taxYear ?? 2026} workbook, filled in — the flight-type dropdown, every
            validation and the locked <code className="text-slate-400">=SUM(G:M)</code> in column F all survive,
            because the template is filled rather than rebuilt. The PDF is the same log in landscape, and is what
            the weekly backup email now attaches.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a href={`/api/export/ba-law.xlsx${qs}`} className="btn-primary text-sm">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
              <path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" />
            </svg>
            Their spreadsheet
          </a>
          <a href={`/api/export/pdf${qs}`} className="btn-ghost text-sm">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
              <path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" />
            </svg>
            Export to PDF
          </a>
        </div>
      </div>

      {error && (
        <div className="mt-4">
          <ErrorNote message={error} />
        </div>
      )}

      {!check ? (
        loading ? (
          <div className="mt-5 space-y-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ) : null
      ) : (
        <>
          <p className="mt-4 text-sm text-slate-300">
            {exportSummary(check)}
            <span className="text-slate-500">
              {' '}
              · {check.policy.periodStart} to {check.policy.periodEnd}
            </span>
          </p>

          {/* The questions that are open with BA Law, answerable here. */}
          <div className="mt-5 grid gap-4 sm:grid-cols-3">
            {QUESTIONS.map((q) => {
              const value = options[q.key];
              const chosen = q.choices.find((c) => c.value === value);
              return (
                <div key={q.key}>
                  <label className="block text-xs font-medium text-slate-400" htmlFor={`balaw-${q.key}`}>
                    {q.question}
                  </label>
                  <select
                    id={`balaw-${q.key}`}
                    value={value}
                    onChange={(e) => setOptions((o) => ({ ...o, [q.key]: e.target.value as never }))}
                    className="mt-1.5 w-full rounded-lg border border-white/10 bg-night-800/70 px-3 py-2 text-sm text-white outline-none transition focus:border-avio/60 focus:ring-2 focus:ring-avio/30"
                  >
                    {q.choices.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1.5 text-[11px] leading-snug text-slate-500">{chosen?.hint}</p>
                </div>
              );
            })}
          </div>
          <p className="mt-2 text-[11px] text-slate-600">
            Open with BA Law as of 13&nbsp;Sep&nbsp;2026. Whichever way you answer is printed on the PDF&rsquo;s
            footer, so the document says how it was read.
          </p>

          {/* Nothing is invented to fill a blank. This is what stays blank, and why. */}
          {issues.length > 0 && (
            <div className="mt-5 rounded-xl border border-white/5 bg-black/20 p-4">
              <h3 className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                What FlightTrack could not fill in
                {blocking > 0 && (
                  <span className="rounded-full bg-rose-500/15 px-2 py-0.5 text-[10px] font-medium normal-case tracking-normal text-rose-300 ring-1 ring-rose-500/30">
                    {blocking} cannot go on the sheet as {blocking === 1 ? 'it stands' : 'they stand'}
                  </span>
                )}
              </h3>
              <ul className="mt-3 space-y-2">
                {issues.map((issue, i) => (
                  <li key={`${issue.code}-${issue.row ?? 'sheet'}-${i}`} className="flex gap-3 text-xs">
                    <span
                      className={`mt-0.5 shrink-0 rounded px-1.5 py-0.5 font-mono text-[10px] ${
                        issue.severity === 'blocks'
                          ? 'bg-rose-500/15 text-rose-300'
                          : 'bg-amber-500/10 text-amber-300/90'
                      }`}
                    >
                      {issue.row ? `row ${issue.row}` : 'sheet'}
                    </span>
                    <span className="text-slate-400">
                      {issue.date && (
                        <span className="text-slate-500">
                          {issue.date} {issue.route} —{' '}
                        </span>
                      )}
                      {issue.message}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
